import { useIsomorphicLayoutEffect } from '@toggle/helpers/src/hooks/use-isomorphic-layout-effect/useIsomorphicLayoutEffect';
import { useEffect, useRef } from 'react';

interface UseDropdownScrollProps<T> {
  dropdownItems: T[];
  shouldResetScrollOnItemsChange: boolean;
  showDropdownItems: boolean;
  activeItemIndex?: number;
  currentAction: 'hover' | 'focus';
  activeSuggestion: number;
}

export const useDropdownScroll = <T>({
  shouldResetScrollOnItemsChange,
  dropdownItems,
  showDropdownItems,
  activeItemIndex,
  currentAction,
  activeSuggestion,
}: UseDropdownScrollProps<T>) => {
  const dropdownContainerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listItemRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (shouldResetScrollOnItemsChange) {
      getScrollingContainer()?.scrollTo({ top: 0 });
    }
  }, [dropdownItems]);

  useIsomorphicLayoutEffect(() => {
    if (showDropdownItems && activeItemIndex !== undefined) {
      scrollToItem('center');
    }
  }, [activeItemIndex, showDropdownItems]);

  useIsomorphicLayoutEffect(() => {
    if (currentAction === 'focus' && activeSuggestion >= 0) {
      scrollToItem();
    }
  }, [activeSuggestion, currentAction]);

  const getScrollingContainer = () => {
    if (!dropdownContainerRef.current || !listRef.current) {
      return null;
    }

    const container = dropdownContainerRef.current;
    const list = listRef.current;
    return container.clientHeight < container.scrollHeight ? container : list;
  };

  const scrollToItem = (block: ScrollLogicalPosition = 'nearest') => {
    const element = listItemRef.current;
    const container = getScrollingContainer();

    if (!element || !container) {
      return;
    }

    if (block === 'center') {
      container.scrollTop =
        element.offsetTop - (container.clientHeight - element.clientHeight) / 2;
    } else {
      element.scrollIntoView({
        block,
      });
    }
  };

  return {
    dropdownContainerRef,
    listRef,
    listItemRef,
  };
};
